/**
 * The Data Type picker reads its options from `value_sets_master`, like every
 * other enum picker in the product, rather than from a list compiled into this
 * bundle. Seeded by `migrations/seed_app_config_value_sets.py`.
 *
 * The value set supplies LABELS and ORDER, not the set of legal types. Each
 * datatype needs a validator in `app_config_schema._TYPE_CHECKS` and a branch in
 * the editor, so a code added to the value set by hand would be offered here and
 * then refused on save. That is why the seed derives its items from the model's
 * DATA_TYPES rather than restating them.
 *
 * `APP_CONFIG_DATA_TYPES` stays as the fallback: before the seed has run these
 * calls return nothing, and a picker with no options would leave the form
 * unusable rather than merely unlabelled.
 */
import { useQuery } from '@tanstack/react-query';
import { valueSetsService } from '../../services/value-sets.service';
import { APP_CONFIG_DATA_TYPES } from '../../services/app-config.service';

export interface Option {
  label: string;
  value: string;
}

/** The value set the Data Type picker reads. Resolved case-insensitively by the
 *  API, so this matches the seeded `appConfigDataType` set. */
export const VALUE_SET_KEY = 'appconfigdatatype';

/** Strip the trailing code in brackets from a value-set label.
 *
 *  The set is seeded as "Text (STRING)", "Whole number (INTEGER)" and so on —
 *  the bracketed half restates the code, which is already the option's value.
 *  The picker shows the readable name alone. */
const withoutCode = (label: string) => label.replace(/\s*\([^)]*\)\s*$/, '').trim();

const FALLBACK: Option[] = APP_CONFIG_DATA_TYPES.map((t) => ({ label: t, value: t }));

export const useAppConfigDataTypes = () => {
  const query = useQuery({
    queryKey: ['value-set-items', VALUE_SET_KEY],
    queryFn: () => valueSetsService.getItems(VALUE_SET_KEY),
    staleTime: 10 * 60 * 1000,
    // A missing value set is a seeding state, not a failure worth retrying on
    // every render of the form.
    retry: false,
  });

  const seeded = !!query.data?.length;
  const options: Option[] = seeded
    ? query.data!.map((item) => ({
        label: withoutCode(item.label || item.code) || item.code,
        value: item.code,
      }))
    : FALLBACK;

  return { options, seeded, loading: query.isLoading };
};
